import { World, DT, makeBody, KIND_ENEMY, KIND_HEAD, KIND_BODY, KIND_ANCHOR } from '@/sim/world';
import { Hero, type PointerState } from '@/sim/hero';
import { impactDamage, reducedMass } from '@/game/damage';
import { Response, tierFor } from '@/feel/response';
import type { WeaponSpec } from '@/content/weapons';

export interface ImpactReport {
  x: number;
  y: number;
  speed: number;
  mass: number;
  killed: boolean;
  wall: boolean;
  grazing: number;
  material: string;
  emitter: string;
}

const ENEMY_HP = 100;
const ANCHOR_HP = 260;
const ANCHOR_STAGES = 3;
const WALL_COEFFICIENT = 0.55;

export class Arena {
  readonly world = new World();
  readonly hero: Hero;
  readonly response = new Response();
  readonly impacts: ImpactReport[] = [];
  readonly halfX: number;
  readonly halfY: number;

  private readonly hp = new Map<number, number>();
  private readonly anchors: number[] = [];
  private readonly anchorMax = new Map<number, number>();
  broken = 0;
  open = false;
  private readonly spec: WeaponSpec;
  private carry = 0;

  constructor(spec: WeaponSpec, halfX: number, halfY: number) {
    this.spec = spec;
    this.halfX = halfX;
    this.halfY = halfY;

    this.world.addWall(-halfX - 2, -halfY - 2, -halfX, halfY + 2);
    this.world.addWall(halfX, -halfY - 2, halfX + 2, halfY + 2);
    this.world.addWall(-halfX - 2, -halfY - 2, halfX + 2, -halfY);
    this.world.addWall(-halfX - 2, halfY, halfX + 2, halfY + 2);
    this.world.addWall(-4, -1.4, 4, 1.4);

    this.hero = new Hero(this.world, spec, -halfX * 0.5, halfY * 0.45);
  }

  spawn(x: number, y: number): number {
    const index = this.world.add(makeBody(x, y, 0.52, 9, 2.2, KIND_ENEMY));
    this.hp.set(index, ENEMY_HP);
    return index;
  }

  anchor(x: number, y: number): number {
    const index = this.world.add(makeBody(x, y, 0.95, 0, 0, KIND_ANCHOR));
    this.hp.set(index, ANCHOR_HP);
    this.anchorMax.set(index, ANCHOR_HP);
    this.anchors.push(index);
    return index;
  }

  anchorState(): { index: number; stage: number; x: number; y: number }[] {
    const out: { index: number; stage: number; x: number; y: number }[] = [];
    for (const index of this.anchors) {
      const body = this.world.bodies[index];
      const left = this.hp.get(index) ?? 0;
      const max = this.anchorMax.get(index) ?? 1;
      const stage = body.alive ? Math.min(ANCHOR_STAGES - 1, Math.floor((1 - left / max) * ANCHOR_STAGES)) : ANCHOR_STAGES;
      out.push({ index, stage, x: body.x, y: body.y });
    }
    return out;
  }

  totalAnchors(): number {
    return this.anchors.length;
  }

  aliveEnemies(): number {
    let n = 0;
    for (const [index] of this.hp) if (this.world.bodies[index].alive) n += 1;
    return n;
  }

  frameUpdate(frameDelta: number, pointer: PointerState): void {
    this.impacts.length = 0;
    this.response.frameUpdate(frameDelta);

    this.carry += frameDelta * this.response.timeScale();
    let steps = 0;
    while (this.carry >= DT && steps < 40) {
      this.hero.fixedUpdate(DT, pointer);
      this.world.step(DT);
      this.resolveContacts();
      this.carry -= DT;
      steps += 1;
    }
  }

  private resolveContacts(): void {
    const bodies = this.world.bodies;
    for (let i = 0; i < this.world.contacts.length; i += 1) {
      const c = this.world.contacts[i];
      if (c.impact <= 0) continue;

      const wall = c.b < 0;
      const a = bodies[c.a];
      const b = wall ? null : bodies[c.b];

      let victim = -1;
      let mass = 0;
      let coefficient = 1;

      if (wall) {
        if (a.kind !== KIND_ENEMY) continue;
        victim = c.a;
        mass = a.invMass > 0 ? 1 / a.invMass : 0;
        coefficient = WALL_COEFFICIENT;
      } else if (a.kind === KIND_HEAD && b && b.kind === KIND_ANCHOR) {
        victim = c.b;
        mass = 1 / a.invMass;
      } else if (b && b.kind === KIND_HEAD && a.kind === KIND_ANCHOR) {
        victim = c.a;
        mass = 1 / b.invMass;
      } else if (a.kind === KIND_HEAD && b && b.kind === KIND_ENEMY) {
        victim = c.b;
        mass = reducedMass(1 / a.invMass, 1 / b.invMass);
      } else if (b && b.kind === KIND_HEAD && a.kind === KIND_ENEMY) {
        victim = c.a;
        mass = reducedMass(1 / b.invMass, 1 / a.invMass);
      } else if (a.kind === KIND_ENEMY && b && b.kind === KIND_ENEMY) {
        victim = c.a;
        mass = reducedMass(1 / a.invMass, 1 / b.invMass);
        coefficient = WALL_COEFFICIENT;
      } else {
        continue;
      }

      const speed = Math.sqrt((2 * c.impact) / Math.max(0.001, mass));
      const dealt = impactDamage(this.spec, speed, mass) * coefficient * ENEMY_HP;
      if (dealt <= 0) continue;

      const left = this.hp.get(victim);
      if (left === undefined) continue;
      const now = left - dealt;
      this.hp.set(victim, now);

      const killed = now <= 0 && left > 0;
      if (killed) {
        bodies[victim].alive = false;
        if (bodies[victim].kind === KIND_ANCHOR) {
          this.broken += 1;
          if (this.broken >= this.anchors.length) this.open = true;
        }
      }

      const severity = Math.min(1, dealt / ENEMY_HP);
      const target = bodies[victim];
      this.response.hit(tierFor(severity, killed), c.nx, c.ny);

      const other = wall ? null : b;
      const rvx = (other ? other.vx : 0) - a.vx;
      const rvy = (other ? other.vy : 0) - a.vy;
      const rv = Math.hypot(rvx, rvy);
      const along = rv > 1e-6 ? Math.abs((rvx / rv) * c.nx + (rvy / rv) * c.ny) : 1;

      this.impacts.push({
        x: target.x,
        y: target.y,
        speed,
        mass,
        killed,
        wall,
        grazing: 1 - Math.min(1, along),
        material: wall ? 'stone' : 'flesh',
        emitter: wall ? `w${c.b}` : `e${victim}`
      });
    }
  }

  heroPosition(): { x: number; y: number } {
    const body = this.world.bodies[this.hero.body];
    return { x: body.x, y: body.y };
  }

  chain(): number[] {
    return [this.hero.body, ...this.hero.joints, this.hero.head];
  }

  headKind(): number {
    return KIND_BODY;
  }
}
